/* eslint-disable react/no-access-state-in-setstate */
import { Dictionary } from "lodash";
import React from "react";
import { DispatchProp } from "react-redux";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { ChoiceModel } from "../../models/ChoiceModel";
import { getSettingBoolean } from "../../models/utilities/settingUtilities";
import { translate } from "../../models/utilities/translationUtilities";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { getRendererProps } from "../renderers/BaseRenderer";
import TextHighlighterRenderer from "../renderers/v2/TextHighlighterRenderer";
import { BaseControl, ControlProps } from "./BaseControl";
import { ElementWithSettings } from "../../models/ElementWithSettings";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";

interface Props extends ControlProps, DispatchProp {}
interface State {
    highlightedWords: Dictionary<any[]>;
    showModal: boolean;
    showDeleteButton: boolean;
    highlightedWordsByCategories: Dictionary<any[]>;
    modalPositions?: DOMRect;
    selectedChoiceModels: ChoiceModel[];
    duplicateWords: string[];
    openEndValue: { [key: string]: any };
    isCategorySelected: boolean;
    isMouseDown: boolean;
    startButtonIndex: number;
    finalButtonIndex: number;
    selectedButtons: string[];
    historyOfHighlighted: any[];
    isDeleting: boolean;
    buttonsRef: any;
    modalRef: any;
    previousHighlightedWordsByCategories: {};
    previousOpenEndValue: {};
}

export class TextHighlighterControl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            highlightedWords: this.generateHighlightedWords(),
            showModal: false,
            selectedChoiceModels: [],
            highlightedWordsByCategories: this.generateHighlightedWordsByCategories(),
            modalPositions: undefined,
            showDeleteButton: false,
            duplicateWords: [],
            openEndValue: {},
            isCategorySelected: false,
            isMouseDown: false,
            startButtonIndex: null,
            finalButtonIndex: null,
            selectedButtons: [],
            historyOfHighlighted: [],
            isDeleting: false,
            buttonsRef: React.createRef<HTMLDivElement>(),
            modalRef: React.createRef(),
            previousHighlightedWordsByCategories: {},
            previousOpenEndValue: {},
        };
    }

    variables = this.props.question.variables.filter((variableItem) =>
        getSettingBoolean(variableItem, "Visible"),
    );

    choiceList = this.variables[0]?.choiceList;

    onReset = () => {
        return null;
    };

    onCloseModal = () => {
        Object.entries(this.state.highlightedWords).forEach(([key, value]) => {
            value.pop();
        });
        this.setState((prevState) => ({
            ...prevState,
            showModal: false,
            isCategorySelected: false,
        }));
    };

    onCancelModal = () => {
        if (this.state.isCategorySelected) {
            const isHighlightedWordsModified =
                JSON.stringify(this.state.previousHighlightedWordsByCategories) !==
                JSON.stringify(this.state.highlightedWordsByCategories);

            const isOpenEndValueModified =
                JSON.stringify(this.state.previousOpenEndValue) !==
                JSON.stringify(this.state.openEndValue);

            if (isHighlightedWordsModified || isOpenEndValueModified) {
                // Only save result if categories or open-end vlaues are modified.
                this.onSaveHighlight();
            }
        }
        this.onCloseModal();
    };

    onDeleteHighlight = () => {
        // Create a copy of the historyOfHighlighted array
        let updatedHistory = [...this.state.historyOfHighlighted];
        const updatedHighlightedWordsByCategories = { ...this.state.highlightedWordsByCategories };
        const updatedOpenEndValue = { ...this.state.openEndValue };

        this.state.selectedChoiceModels.forEach((selectedChoiceModel) => {
            // Modify the copy, not the state directly
            updatedHistory = updatedHistory
                .map((highlightedCollection) => {
                    const modifiedCollection = {
                        selectedButtons: highlightedCollection.selectedButtons,
                        selectedChoiceModels: highlightedCollection.selectedChoiceModels.filter(
                            (choiceModel) => choiceModel.value !== selectedChoiceModel.value,
                        ),
                        openEndValue: Object.keys(highlightedCollection.openEndValue).reduce(
                            (acc, key) => {
                                if (key !== selectedChoiceModel.value) {
                                    acc[key] = highlightedCollection.openEndValue[key];
                                }
                                return acc;
                            },
                            {},
                        ),
                    };
                    return modifiedCollection;
                })
                .filter(
                    (highlightedCollection) =>
                        highlightedCollection.selectedChoiceModels.length !== 0,
                );

            // Modify highlightedWordsByCategories
            Object.entries(updatedHighlightedWordsByCategories).forEach(([key, value]) => {
                const choiceIndex = value.findIndex(
                    (item) => selectedChoiceModel === item.choiceModel,
                );
                if (this.props.mentions[key]) {
                    const mentionIndex = this.props.mentions[key].findIndex(
                        (item) => selectedChoiceModel.value === item.value,
                    );
                    if (mentionIndex > -1) {
                        this.props.mentions[key].splice(mentionIndex, 1);
                    }
                }
                if (choiceIndex > -1) {
                    updatedHighlightedWordsByCategories[key].splice(choiceIndex, 1);
                }
            });

            // Modify openEndValue
            delete updatedOpenEndValue[selectedChoiceModel.value];
        });

        this.setState(
            {
                historyOfHighlighted: updatedHistory,
                highlightedWordsByCategories: updatedHighlightedWordsByCategories,
                openEndValue: updatedOpenEndValue,
                isDeleting: true,
            },
            () => {
                this.onSaveHighlight();
                this.setState({
                    isDeleting: false,
                });
            },
        );
    };

    toggleIsCategorySelected = (isSelected: boolean, enable: boolean) => {
        this.setState({ isCategorySelected: isSelected });
    };

    renderQuestion() {
        return (
            <TextHighlighterRenderer
                highlightedWordsByCategories={this.state.highlightedWordsByCategories}
                {...getRendererProps(this.props)}
                onHighlight={this.onHighlight}
                highlightedWords={this.state.highlightedWords}
                showModal={this.state.showModal}
                mentions={this.props.mentions}
                onReset={this.onReset}
                onCancelModal={this.onCancelModal}
                modalPositions={this.state.modalPositions}
                onOpenEndBlur={this.onOpenEndBlur}
                onOpenEndChange={this.onOpenEndChange}
                showDeleteButton={this.state.showDeleteButton}
                onDeleteHighlight={this.onDeleteHighlight}
                selectedChoiceModels={this.state.selectedChoiceModels}
                isChoiceAlreadyHighlighted={this.isChoiceAlreadyHighlighted}
                duplicateWords={this.state.duplicateWords}
                openEndValue={this.state.openEndValue}
                variables={this.variables}
                toggleIsCategorySelected={this.toggleIsCategorySelected}
                handleMouseDown={this.handleMouseDown}
                handleMouseMove={this.handleMouseMove}
                selectedButtons={this.state.selectedButtons}
                buttonsRef={this.state.buttonsRef}
                modalRef={this.state.modalRef}
                handleTouchStart={this.handleTouchStart}
                handleTouchMove={this.handleTouchMove}
                handleTouchEnd={this.handleTouchEnd}
            />
        );
    }

    getWordPosition = (button: HTMLSpanElement) => {
        const positionStr = button.getAttribute("data-position");
        if (!positionStr) {
            return null;
        }
        const position = parseInt(positionStr, 10);
        // eslint-disable-next-line no-restricted-globals
        if (isNaN(position)) {
            return null;
        }
        return position;
    };

    handleTouchStart = (e: TouchEvent) => {
        e.preventDefault();
        const touch = e.touches[0];
        const initialButton = document.elementFromPoint(
            touch.clientX,
            touch.clientY,
        ) as HTMLElement;

        if (initialButton instanceof HTMLSpanElement) {
            initialButton.setAttribute(
                "data-start-index",
                String(this.getWordPosition(initialButton)),
            );
            initialButton.setAttribute("data-selected", "true");
            initialButton.style.backgroundColor = "lightgrey";
        }
    };

    handleTouchMove = (e: TouchEvent) => {
        const touch = e.touches[0];
        const currentButton = document.elementFromPoint(
            touch.clientX,
            touch.clientY,
        ) as HTMLElement;

        if (currentButton instanceof HTMLSpanElement) {
            e.preventDefault();
            const currentButtonIndex = this.getWordPosition(currentButton);
            const buttons = Array.from(
                this.state.buttonsRef.current.querySelectorAll("[data-position]"),
            ) as HTMLElement[];

            // Retrieve the start index from the attribute we set during touch start
            const startButton = this.state.buttonsRef.current.querySelector("[data-start-index]");
            const startButtonIndex = startButton
                ? Number(startButton.getAttribute("data-start-index"))
                : null;

            if (startButtonIndex !== null) {
                const startIndex = Math.min(currentButtonIndex, startButtonIndex);
                const endIndex = Math.max(currentButtonIndex, startButtonIndex);

                for (let i = 0; i < buttons.length; i++) {
                    const buttonPosition = this.getWordPosition(buttons[i]);
                    if (buttonPosition >= startIndex && buttonPosition <= endIndex) {
                        buttons[i].setAttribute("data-selected", "true");
                        const selectedChoiceValue = buttons[i].id.split("-")[3];
                        const selectedChoice = this.choiceList.find(
                            (choice) => choice.value === selectedChoiceValue,
                        );
                        if (getSettingBoolean(selectedChoice, "Highlightable")) {
                            buttons[i].style.backgroundColor = "lightgrey";
                        }
                    } else {
                        buttons[i].removeAttribute("data-selected");
                        buttons[i].style.backgroundColor = "";
                    }
                }
            }
        }
    };

    handleTouchEnd = (e: TouchEvent) => {
        e.preventDefault();
        // Get the array of all buttons
        const buttons = Array.from(
            this.state.buttonsRef.current.querySelectorAll("[data-position]"),
        ) as HTMLElement[];

        // Get the id of all buttons with 'lightgrey' background color
        const selectedButtons = buttons.filter((button) => button.hasAttribute("data-selected"));
        // Map selected buttons to their respective position indices
        const selectedButtonIndices = selectedButtons.map((button) => this.getWordPosition(button));

        // Get the id of the final button
        const finalButtonIndex = Math.max(...selectedButtonIndices);

        this.setState(
            {
                isMouseDown: false,
                startButtonIndex: null,
                finalButtonIndex,
                selectedButtons: selectedButtons.map((button) => button.id),
            },
            () => {
                if (selectedButtons.length > 0) {
                    // Delay showing the modal to allow rendering to continue
                    setTimeout(() => {
                        this.onHighlight();
                    }, 100);
                }
            },
        );

        // Reset color of all buttons
        // eslint-disable-next-line no-return-assign, no-param-reassign
        buttons.forEach((button) => (button.style.backgroundColor = ""));
        buttons.forEach((button) => button.removeAttribute("data-selected"));

        // At the end of the touch event, remove the 'data-start-index' attribute from the initial button
        const startButton = this.state.buttonsRef.current.querySelector("[data-start-index]");
        if (startButton) {
            startButton.removeAttribute("data-start-index");
        }
    };

    handleMouseDown = (e: MouseEvent) => {
        const initialButton = e.target as HTMLSpanElement;
        this.setState({
            isMouseDown: true,
            startButtonIndex: this.getWordPosition(initialButton),
            finalButtonIndex: this.getWordPosition(initialButton),
            selectedButtons: [initialButton.id],
        });
    };

    handleMouseMove = (e: MouseEvent) => {
        if (!this.state.isMouseDown) {
            return;
        }

        const currentButton = e.target as HTMLSpanElement;
        const currentButtonIndex = this.getWordPosition(currentButton);
        const range = {
            start: Math.min(this.state.startButtonIndex, currentButtonIndex),
            end: Math.max(this.state.startButtonIndex, currentButtonIndex),
        };
        const selectedButtons = this.getButtonsInRange(range.start, range.end);
        this.setState({ selectedButtons, finalButtonIndex: currentButtonIndex });
    };

    handleMouseUp = () => {
        this.setState({ isMouseDown: false });
        this.setState({ startButtonIndex: null });
        if (this.state.selectedButtons.length > 0) {
            this.onHighlight();
        }
    };

    getButtonsInRange = (startIndex: number, endIndex: number): string[] => {
        const selectedButtons = [];
        const buttons = this.state.buttonsRef.current.querySelectorAll("[data-position]");

        for (let i = 0; i < buttons.length; i++) {
            const button = buttons[i];
            // eslint-disable-next-line radix
            const position = parseInt(button.getAttribute("data-position") || "");
            // eslint-disable-next-line no-restricted-globals
            if (!isNaN(position) && position >= startIndex && position <= endIndex) {
                selectedButtons.push(buttons[i].id);
            }
        }
        return selectedButtons;
    };

    handleOutsideButtonsClick = (e: MouseEvent) => {
        const targetElement = e.target as HTMLElement;
        if (targetElement) {
            if (!targetElement.id.startsWith("text-highlight-choice-")) {
                this.setState({ selectedButtons: [] });
            }
        }
    };

    checkIfSubSelectionByMentions = () => {
        // this is used when user come back to text highlighter to modify their answer, but the status of history is not here.
        // need to investigate how to keep it in future
        const values = this.state.selectedChoiceModels.map((choice) => choice.value);
        return Object.values(this.props.mentions).some((mentionList) =>
            values.every((value) => mentionList.some((mention) => mention.value === value)),
        );
    };

    onHighlight = () => {
        if (this.state.showModal) {
            return;
        }

        const isChoiceAlreadyHighlightedArray = [];
        const modalPositionId = this.state.selectedButtons.find(
            (element) =>
                document.getElementById(element).getAttribute("data-position") ===
                this.state.finalButtonIndex.toString(),
        );

        // if there still someting get selected
        if (this.state.selectedChoiceModels !== undefined) {
            this.setState({ selectedChoiceModels: [] });
        }

        Object.entries(this.state.selectedButtons).forEach(([index, buttonId]) => {
            const selectedChoiceValue = buttonId.split("-")[3];
            this.state.highlightedWords[selectedChoiceValue].splice(0);
            const selectedChoice = this.choiceList.find(
                (choice) => choice.value === selectedChoiceValue,
            );

            // make selected choice model here
            if (getSettingBoolean(selectedChoice, "Highlightable")) {
                this.state.selectedChoiceModels.push(selectedChoice);
                const freeSelectionEnabled = this.choiceList.every(
                    (choice) =>
                        !getSettingBoolean(choice, "Highlightable") &&
                        !getSettingBoolean(choice, "IsGroup"),
                );

                const choiceText = translate(selectedChoice, "TEXT", this.props.language);
                const isChoiceHighlighted = this.isChoiceAlreadyHighlighted(
                    selectedChoice,
                    undefined,
                );
                isChoiceAlreadyHighlightedArray.push(isChoiceHighlighted);

                if (
                    !isChoiceHighlighted &&
                    (getSettingBoolean(selectedChoice, "Highlightable") || freeSelectionEnabled)
                ) {
                    this.state.highlightedWords[selectedChoice.value].push(choiceText);
                }
            }
        });
        const subsetSelection = this.state.historyOfHighlighted.some((highlightedSelection) => {
            const previousButtons = highlightedSelection.selectedChoiceModels;
            return (
                this.state.selectedChoiceModels.length <= previousButtons.length &&
                this.state.selectedChoiceModels.every((item) => previousButtons.includes(item))
            );
        });

        this.setState({
            showDeleteButton: isChoiceAlreadyHighlightedArray.includes(true),
            showModal: true,
            modalPositions: this.state.buttonsRef.current
                .querySelector(`#${modalPositionId}`)
                .getBoundingClientRect(),
            isCategorySelected: subsetSelection || this.checkIfSubSelectionByMentions(),
        });
    };

    /*
        Each Selection of Highlight (a sentence, a word) are will be at same group (rpsMatrix)
    */
    getMatrixIdByChoiceValue = (currentChoiceValue) => {
        const choiceGroupIndex = this.state.historyOfHighlighted.findIndex((highlightedGroup) =>
            highlightedGroup.selectedChoiceModels.some(
                (choice) => choice.value === currentChoiceValue,
            ),
        );
        let choiceOpenEndValue =
            this.state.historyOfHighlighted[choiceGroupIndex].openEndValue[currentChoiceValue];

        if (typeof choiceOpenEndValue === "undefined") {
            choiceOpenEndValue = null;
        }

        return [choiceGroupIndex + 1, choiceOpenEndValue];
    };

    removeMentionAcrossMatrices = (variableName, valueToRemove, newMatrixId) => {
        // Iterate over all matrices from 1 to newMatrixId and remove mentions with the provided value
        for (let matrixId = 1; matrixId < newMatrixId; matrixId++) {
            this.setMentions(
                this.props,
                variableName,
                (mentions) => mentions.pull(valueToRemove),
                matrixId, // Use the current matrixId in the loop
                true,
            );
        }
    };

    setChoicesAsMentions = (element: ElementWithSettings, choices: ChoiceModel[]) => {
        const values = choices.map((choice) => choice.value);
        if (values.length) {
            values.forEach((value) => {
                // For future
                // const [currentMatrixId, currentOpenEndValue] = this.getMatrixIdByChoiceValue(value);
                // this.removeMentionAcrossMatrices(element.name, value, currentMatrixId);
                const currentOpenEndValue = this.state.openEndValue[value];
                this.setMentions(
                    this.props,
                    element.name,
                    (mentions) => mentions.pull(value).add(value, currentOpenEndValue),
                    undefined,
                    true,
                );
            });
        }
    };

    saveCurrentHighlight = () => {
        Object.entries(this.state.highlightedWordsByCategories).forEach(
            ([key, highlightedWords]) => {
                const variable = this.props.question.variables.find((x) => x.name === key);
                if (highlightedWords.length) {
                    const values = highlightedWords
                        .filter((item) => item.choiceModel !== undefined)
                        .flatMap((item) => item.choiceModel);
                    if (values.length) {
                        this.setChoicesAsMentions(variable, values);
                    } else {
                        this.setMentions(this.props, variable.name, (m) => m.clear());
                    }
                }
            },
        );
    };

    onSaveHighlight = () => {
        this.onCloseModal();
        Object.entries(this.state.highlightedWords).forEach(([key, value]) => {
            value.splice(0);
        });
        const previousHighlightedWordsByCategories = JSON.parse(
            JSON.stringify(this.state.highlightedWordsByCategories),
        );
        const previousOpenEndValue = JSON.parse(JSON.stringify(this.state.openEndValue));
        if (!this.state.isDeleting) {
            // Need to delete first to have new matrix Id
            let updatedHistory = [...this.state.historyOfHighlighted];
            this.state.selectedChoiceModels.forEach((selectedChoiceModel) => {
                // Modify the copy, not the state directly
                updatedHistory = updatedHistory
                    .map((highlightedCollection) => {
                        const modifiedCollection = {
                            selectedButtons: highlightedCollection.selectedButtons,
                            selectedChoiceModels: highlightedCollection.selectedChoiceModels.filter(
                                (choiceModel) => choiceModel.value !== selectedChoiceModel.value,
                            ),
                            openEndValue: Object.keys(highlightedCollection.openEndValue).reduce(
                                (acc, key) => {
                                    if (key !== selectedChoiceModel.value) {
                                        acc[key] = highlightedCollection.openEndValue[key];
                                    }
                                    return acc;
                                },
                                {},
                            ),
                        };
                        return modifiedCollection;
                    })
                    .filter(
                        (highlightedCollection) =>
                            highlightedCollection.selectedChoiceModels.length !== 0,
                    );
            });

            const selectedChoiceOpenEndValues = {};
            this.state.selectedChoiceModels.forEach((selectedChoiceModel) => {
                const choiceValue = selectedChoiceModel.value;
                const openEndValue = this.state.openEndValue[choiceValue];
                if (openEndValue !== undefined) {
                    selectedChoiceOpenEndValues[choiceValue] = openEndValue;
                }
            });

            const newHighlightedSelection = {
                selectedButtons: [...this.state.selectedButtons],
                selectedChoiceModels: [...this.state.selectedChoiceModels],
                openEndValue: { ...selectedChoiceOpenEndValues },
            };

            // Create deep copies of the state objects
            const copiedSelectedButtons = [...newHighlightedSelection.selectedButtons];
            const copiedSelectedChoiceModels = [...newHighlightedSelection.selectedChoiceModels];
            const copiedOpenEndValue = { ...newHighlightedSelection.openEndValue };
            this.setState(
                () => ({
                    selectedChoiceModels: [],
                    historyOfHighlighted: [
                        ...updatedHistory,
                        {
                            selectedButtons: copiedSelectedButtons,
                            selectedChoiceModels: copiedSelectedChoiceModels,
                            openEndValue: copiedOpenEndValue,
                        },
                    ],
                    previousHighlightedWordsByCategories,
                    previousOpenEndValue,
                }),
                () => {
                    this.saveCurrentHighlight();
                },
            );
        } else {
            this.saveCurrentHighlight();
        }
    };

    getNumberOfHighlightedChoices = () => {
        return Object.values(this.props.mentions).reduce((acc, arr) => acc + arr.length, 0);
    };

    validate(): ValidationResultModel {
        const { question, useClientValidation, questionnaire } = this.props;

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const [variable] = question.variables;
        const required = this.isAnswerRequired(questionnaire, question);
        const numberOfHighlightedChoices = this.getNumberOfHighlightedChoices();

        if (required && numberOfHighlightedChoices === 0) {
            return new ValidationBuilder().fail(variable.name, "IncompleteAnswer").build();
        }

        if (numberOfHighlightedChoices > 0) {
            const min = this.minValue();
            const max = this.maxValue();

            // Test for minimum and maximum values.
            if (min !== null && min > 0 && numberOfHighlightedChoices < min) {
                return new ValidationBuilder()
                    .fail(variable.name, "InvalidChoicesMin", { min: String(min) })
                    .build();
            }
            if (max !== null && max > 0 && numberOfHighlightedChoices > max) {
                return new ValidationBuilder()
                    .fail(variable.name, "InvalidChoicesMax", { max: String(max) })
                    .build();
            }
        }
        return new ValidationBuilder().build();
    }

    onOpenEndChange = (answer: any, event: any) => {
        const { value } = event.target;
        if (this.state.isCategorySelected) {
            // Allow changing the open end value only if isCategorySelected is true
            Object.entries(this.state.selectedChoiceModels).forEach(([id, selectedChoiceModel]) => {
                this.setState((prevState) => {
                    const newState = {
                        ...prevState,
                        openEndValue: {
                            ...prevState.openEndValue,
                            [selectedChoiceModel.value]: this.truncateOpenEnd(answer, value),
                        },
                    };
                    return newState;
                });
            });
        }
        // is modal modifed true
    };

    onOpenEndBlur = (answer: any) => {
        return null;
    };

    generateHighlightedWordsByCategories(): Dictionary<any[]> {
        const highlightedWordsModelList: Dictionary<any[]> = {};
        this.variables.forEach((variable) => {
            highlightedWordsModelList[variable.name] = [
                { highlightedWord: new RegExp("^$"), choiceModel: undefined },
            ];
        });
        return highlightedWordsModelList;
    }

    generateHighlightedWords(): Dictionary<any[]> {
        const highlightedWordsModelList: Dictionary<any[]> = {};
        if (this.choiceList) {
            this.choiceList.forEach((choice) => {
                highlightedWordsModelList[choice.value] = [new RegExp("^$")];
            });
        }
        return highlightedWordsModelList;
    }

    isChoiceAlreadyHighlighted = (selectedChoice: ChoiceModel, variableName: string): boolean => {
        let result = false;
        if (variableName !== undefined) {
            result = this.state.highlightedWordsByCategories[variableName].some(
                (item) => item.choiceModel === selectedChoice,
            );
        } else {
            result = Object.entries(this.state.highlightedWordsByCategories).some(([key, value]) =>
                value.some((item) => item.choiceModel === selectedChoice),
            );
        }
        return result;
    };

    componentDidMount() {
        const { question } = this.props;
        if (question !== this.renderedQuestion) {
            this.questionFirstRender((this.renderedQuestion = question), this.props);
        }
        window.addEventListener("mouseup", this.handleMouseUp);
        window.addEventListener("mousedown", this.handleOutsideButtonsClick);
        window.addEventListener("touchmove", this.handleTouchMove, { passive: false });
    }

    componentWillUnmount() {
        const { question } = this.props;
        this.finalizeQuestion(question, this.props);
        window.removeEventListener("mouseup", this.handleMouseUp);
        window.removeEventListener("mousedown", this.handleOutsideButtonsClick);
        window.removeEventListener("touchmove", this.handleTouchMove);
    }
}
